import { IntegrationAppClient } from '@membranehq/react';

export enum IntegrationKey {
  HUBSPOT = 'hubspot',
  SALESFORCE = 'salesforce',
  GOHIGHLEVEL = 'gohighlevel',
  LEADDOCKET = 'lead-docket',
  MONDAY = 'monday',
  TLD_CRM = 'total-lead-domination',
  ACTIVECAMPAIGN = 'activecampaign',
  SALESFORCE_PARDOT = 'salesforce-pardot',
}

export const NON_AUTO_SYNCABLE_PROVIDERS: readonly IntegrationKey[] = [
  IntegrationKey.MONDAY,
  IntegrationKey.SALESFORCE_PARDOT,
];

export const PROVIDERS_SUPPORTING_SEGMENTED_CONTACTS: readonly IntegrationKey[] =
  [IntegrationKey.HUBSPOT];

export const PROVIDERS_WITH_BOARD_CONFIGURATION: readonly IntegrationKey[] = [
  IntegrationKey.MONDAY,
];

export enum DataSourceKey {
  CONTACTS = 'contacts',
}

export enum IntegrationAppActionKey {
  SEND_EMAIL = 'send_email',
  SEND_MESSAGE = 'send_message',
}

export enum ContactRecordField {
  ID = 'id',
  PRIMARY_PHONE = 'primaryPhone',
  PRIMARY_EMAIL = 'primaryEmail',
  FULL_NAME = 'fullName',
}

// https://console.integration.app/docs/membrane/references/udm
export interface ContactRecord {
  id: string;
  fields: {
    [ContactRecordField.ID]: string;
    [ContactRecordField.PRIMARY_PHONE]?: string;
    [ContactRecordField.PRIMARY_EMAIL]?: string;
    [ContactRecordField.FULL_NAME]?: string;
  };
}

export interface ListActionResponse<T> {
  records: T[];
  cursor?: string;
}

export type SegmentRecord = {
  id: string;
  name: string;
};

export interface MondayListParams {
  board_id?: string;
  group_id?: string | null;
}

export async function listAction<T>({
  client,
  integrationKey,
  dataSourceKey,
  segmentId,
  mondayParams,
  cursor,
  query,
  maxRecords,
  onError,
}: {
  client: IntegrationAppClient;
  integrationKey: string | null;
  dataSourceKey: string;
  segmentId?: string;
  mondayParams?: MondayListParams;
  cursor?: string;
  query?: string;
  maxRecords?: number;
  onError?: (error: Error) => void;
}): Promise<ListActionResponse<T>> {
  if (!integrationKey) {
    return { records: [] };
  }

  try {
    const actionKey = segmentId
      ? `list-${dataSourceKey}-in-segment`
      : `list-${dataSourceKey}`;
    const response = await client
      .connection(integrationKey)
      .action(actionKey)
      .run({
        cursor,
        query,
        ...(segmentId ? { segmentId } : {}),
        ...(mondayParams || {}),
      });

    const records = response.output.records as T[];
    const limitedRecords = maxRecords ? records.slice(0, maxRecords) : records;

    return {
      records: limitedRecords,
      cursor: response.output.cursor,
    };
  } catch (error) {
    console.error(`Error fetching ${dataSourceKey} from CRM:`, error);
    const errorObj =
      error instanceof Error ? error : new Error('Unknown error occurred');
    onError?.(errorObj);
    throw errorObj;
  }
}

export async function listSegments({
  client,
  integrationKey,
  dataSourceKey,
  cursor,
  query,
  maxRecords,
  onError,
}: {
  client: IntegrationAppClient;
  integrationKey: string | null;
  dataSourceKey: string;
  cursor?: string;
  query?: string;
  maxRecords?: number;
  onError?: (error: Error) => void;
}): Promise<ListActionResponse<SegmentRecord>> {
  if (!integrationKey) {
    return { records: [] };
  }

  try {
    const response = await client
      .connection(integrationKey)
      .action(`list-segments-with-${dataSourceKey}`)
      .run({
        cursor,
        query,
      });

    const records = response.output.records as SegmentRecord[];
    const limitedRecords = maxRecords ? records.slice(0, maxRecords) : records;

    return {
      records: limitedRecords,
      cursor: response.output.cursor,
    };
  } catch (error) {
    console.error(`Error fetching segments for ${dataSourceKey}:`, error);
    const errorObj =
      error instanceof Error ? error : new Error('Unknown error occurred');
    onError?.(errorObj);
    throw errorObj;
  }
}
